import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Building, CalendarCheck, ShieldCheck, Car, Sparkles, ArrowRight } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/ui-kit/Reveal";
import QuoteButton from "@/components/ui-kit/QuoteButton";
import { EASE } from "@/lib/site";

const SERVICES = [
  { id: "security-guards", to: "/services/security-service", icon: Shield, title: "Security Service", img: "/images/service-security-guards.jpg", desc: "S4s Enterprises offers a tiered security service module designed to address various threat levels, venue sizes, and operational requirements. Our personnel operate strictly within defined operational workflows, ensuring safety without disrupting your daily business." },
  { id: "facility-management", to: "/services/housekeeping-service", icon: Building, title: "Housekeeping Service", img: "/images/service-facility.jpg", desc: "Maintaining a pristine environment is critical for business reputation and employee health. We deploy heavily trained housekeeping personnel equipped to handle daily deep cleaning, sanitization, and facility maintenance. Our programs are customized for demanding corporate, commercial, and large-scale residential spaces." },
  { id: "event-management", to: "/services/event-management", icon: CalendarCheck, title: "Event Management", img: "/images/service-surveillance.jpg", desc: "Great events are remembered for the experience—not the chaos behind the scenes. With S4s Enterprises Pvt. Ltd., your event is in expert hands:",
    points: [{ icon: ShieldCheck, text: "Tight Security & VIP Bodyguards" }, { icon: Car, text: "Swift, Courteous Valet Parking" }, { icon: Sparkles, text: "Immaculate Venue Housekeeping" }] },
];

const Card = ({ s, i }) => (
  <motion.article data-testid={`service-card-${s.id}`} initial={{ opacity: 0, y: 60 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
    className="group relative bg-white flex flex-col h-full">
    <Link to={s.to} className="flex flex-col h-full" data-testid={`service-card-link-${s.id}`}>
      <div className="relative aspect-[16/10] overflow-hidden chamfer-sm">
        <img src={s.img} alt={s.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
        <div className="absolute inset-0 bg-[#222A88]/0 group-hover:bg-[#222A88]/30 transition-colors duration-500" />
        <div className="absolute left-0 bottom-0 w-14 h-14 bg-[var(--red)] grid place-items-center text-white">
          <s.icon className="w-6 h-6" />
        </div>
        <span className="absolute right-4 top-4 font-display italic font-black text-white text-4xl drop-shadow">0{i + 1}</span>
      </div>
      <div className="flex-1 flex flex-col pt-7 pb-2 border-b-2 border-slate-200 group-hover:border-[var(--red)] transition-colors duration-500">
        <h3 className="font-display italic font-extrabold uppercase text-3xl text-[var(--ink)] leading-none">{s.title}</h3>
        <p className={`mt-4 text-slate-600 leading-relaxed text-sm sm:text-base ${s.points ? "" : "flex-1"}`}>{s.desc}</p>
        {s.points && (
          <ul className="mt-4 space-y-2.5 flex-1">
            {s.points.map((p) => (
              <li key={p.text} className="flex items-center gap-3 text-sm sm:text-base font-semibold text-[var(--ink)]">
                <span className="w-8 h-8 shrink-0 grid place-items-center bg-[#222A88]/10 text-[var(--navy)]"><p.icon className="w-4 h-4" /></span>{p.text}
              </li>
            ))}
          </ul>
        )}
        <span className="mt-6 mb-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[var(--navy)]">
          Learn more <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-2" />
        </span>
      </div>
    </Link>
  </motion.article>
);

export default function Services() {
  return (
    <section data-testid="services-grid-section" className="relative bg-[var(--paper)] py-24 sm:py-32 overflow-hidden">
      <div aria-hidden className="absolute -right-24 top-20 w-[50%] h-[3px] bg-[#D91219]/70 -rotate-[14deg]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <Reveal>
            <Eyebrow>Our services</Eyebrow>
            <h2 className="mt-5 font-display italic font-black uppercase text-5xl sm:text-6xl leading-[0.9] text-[var(--ink)] max-w-2xl">
              Total protection, <span className="text-[var(--red)]">one partner.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <QuoteButton testId="services-view-all-button" to="/services" label="View all services" variant="navy" small />
          </Reveal>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 items-stretch">
          {SERVICES.map((s, i) => <Card key={s.id} s={s} i={i} />)}
        </div>
      </div>
    </section>
  );
}
